import isEmpty from 'lodash/isEmpty';

/**
 * !!! IMPORTANT !!!
 *
 * This is legacy tooltips manager used in various parts of the application.
 * It manages tooltips activation on elements with data attributes.
 *
 * New tooltips should use DivTooltip from wows-react-ui-kit.
 */

type TooltipId = string;

interface TooltipController {
  activate: (content?: string) => void;
  move: (e: MouseEvent) => void;
  hide: () => void;
}

interface TooltipTargetEl extends HTMLElement {
  dataset: DOMStringMap & {
    for?: TooltipId;
    tip?: string;
  };
}

class TooltipManager {
  private tooltips: Record<TooltipId, TooltipController> = Object.create(null) as Record<TooltipId, TooltipController>;
  private activeTooltip: TooltipController | null = null;
  private tooltipTarget: TooltipTargetEl | null = null;

  addTooltip(id: TooltipId, tooltip: TooltipController) {
    if (this.tooltips[id]) {
      throw new Error(`duplicate tooltip id - ${id}`);
    }

    if (isEmpty(this.tooltips)) {
      document.addEventListener('mouseover', this.onMouseOver);
    }

    this.tooltips[id] = tooltip;
  }

  removeTooltip(id: TooltipId) {
    const tooltip = this.tooltips[id];

    if (!tooltip) {
      console.warn(`try delete tooltip id "${id}" that does not exist`);
      return;
    }

    if (tooltip === this.activeTooltip) {
      this.destroyActiveTooltip();
    }

    delete this.tooltips[id];

    if (isEmpty(this.tooltips)) {
      document.removeEventListener('mouseover', this.onMouseOver);
    }
  }

  updateActiveTooltip() {
    if (!this.activeTooltip || !this.tooltipTarget) {
      return;
    }

    const id = this.tooltipTarget.dataset.for;
    if (!id) {
      return;
    }

    const tooltip = this.tooltips[id];
    if (tooltip && tooltip !== this.activeTooltip) {
      this.activateTooltip(tooltip, this.tooltipTarget);
    }
  }

  private onMouseLeave = (e: MouseEvent) => {
    const currentTarget = e.currentTarget;
    if (!(currentTarget instanceof HTMLElement)) {
      this.destroyActiveTooltip();
      return;
    }

    const related = e.relatedTarget instanceof HTMLElement ? e.relatedTarget : null;
    const relatedTarget = related ? this.getClosestParentWithTooltip(related) : null;

    const cur = currentTarget as TooltipTargetEl;

    if (
      relatedTarget &&
      cur.dataset.for &&
      cur.dataset.tip &&
      cur.dataset.for === relatedTarget.dataset.for &&
      cur.dataset.tip === relatedTarget.dataset.tip
    ) {
      this.removeListeners();
      this.addListeners(relatedTarget);
      return;
    }

    this.destroyActiveTooltip();
  };

  private onMouseOver = (e: MouseEvent) => {
    const rawTarget = e.target;
    if (!(rawTarget instanceof HTMLElement)) {
      return;
    }

    const target = this.getClosestParentWithTooltip(rawTarget);
    const id = target?.dataset.for;

    if (!target || !id) {
      return;
    }

    const tooltip = this.tooltips[id];
    if (tooltip && this.activeTooltip !== tooltip) {
      this.activateTooltip(tooltip, target);
    }
  };

  private onMouseMove = (e: MouseEvent) => {
    this.activeTooltip?.move(e);
  };

  private onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.activeTooltip) {
      e.preventDefault();
      this.destroyActiveTooltip();
    }
  };

  private activateTooltip(tooltip: TooltipController, target: TooltipTargetEl) {
    if (this.activeTooltip) {
      this.destroyActiveTooltip();
    }

    this.activeTooltip = tooltip;
    this.activeTooltip.activate(target.dataset.tip);

    this.addListeners(target);

    document.addEventListener('keydown', this.onEscape, true);

    const app = document.getElementById('app');
    app?.addEventListener('scroll', this.destroyActiveTooltip);
  }

  public destroyActiveTooltip = () => {
    if (!this.activeTooltip) {
      return;
    }

    this.activeTooltip.hide();
    this.activeTooltip = null;

    document.removeEventListener('keydown', this.onEscape, true);

    const app = document.getElementById('app');
    app?.removeEventListener('scroll', this.destroyActiveTooltip);

    this.removeListeners();
  };

  private addListeners(target: TooltipTargetEl) {
    this.tooltipTarget = target;

    target.addEventListener('contextmenu', this.destroyActiveTooltip);
    target.addEventListener('mousemove', this.onMouseMove, true);
    target.addEventListener('mouseleave', this.onMouseLeave);
  }

  private removeListeners() {
    const target = this.tooltipTarget;
    if (!target) {
      return;
    }

    target.removeEventListener('contextmenu', this.destroyActiveTooltip);
    target.removeEventListener('mousemove', this.onMouseMove, true);
    target.removeEventListener('mouseleave', this.onMouseLeave);

    this.tooltipTarget = null;
  }

  private getClosestParentWithTooltip(start: HTMLElement): TooltipTargetEl | null {
    let el: HTMLElement | null = start;

    while (el) {
      const candidate = el as TooltipTargetEl;
      if (candidate.dataset?.for) {
        return candidate;
      }
      el = el.parentElement;
    }

    return null;
  }
}

const manager = new TooltipManager();

export default manager;
